<template>
	<modal name="playlists-settings" :width="300" :height="500">
		<div class="playlists-settings-modal">
			<div class="playlists-header">
				<h1>{{ $t('playlist.playlists') }}</h1>
				<button type="button" class="button-icon button-icon-add" @click="createPlaylist"><IconPlus /><span>{{ $t('playlist.createButton') }}</span></button>
				<IconTimes class="close-modal" @click.native="$modal.hide('playlists-settings')" />
			</div>

			<div class="playlists-container">
				<div class="playlists">
					<div class="playlist" v-for="playlist in playlists" v-bind:key="playlist.id">
						<div class="left">
							<input type="text" ref="editPlaylistInput" name="editPlaylist" v-model="editingPlaylist.title" v-if="editingPlaylist !== null && editingPlaylist.id === playlist.id" />
							<span v-else>{{ playlist.title }}</span>
						</div>
						<div class="right">
							<button type="button" class="button-icon button-icon-check" @click="savePlaylist" v-if="editingPlaylist !== null && editingPlaylist.id === playlist.id"><IconCheck /></button>
							<template v-else>
								<button type="button" class="button-icon button-icon-edit" @click="editPlaylist(playlist)"><IconPen /></button>
								<div class="playlist-color-select" @click="changeColor(playlist)" :style="stylePlaylistColor(playlist)">
									<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
										 viewBox="0 0 422.555 422.555" style="enable-background:new 0 0 422.555 422.555;" xml:space="preserve">
										<path d="M419.061,11.643c-2.223-2.127-5.45-3.139-9.544-3.139c-32.927,0-122.005,65.392-196.859,141.77
										c-42.696,43.557-64.478,74.066-72.961,96.074c6.455,2.162,13.001,5.199,19.671,9.167c5.219,3.105,10.092,6.77,14.468,10.88
										c0.006,0.002,0.008,0.004,0.014,0.006c8.528,8.007,14.971,17.444,19.188,27.578c21.773-9.709,51.271-32.1,92.405-74.059
										C369.608,134.048,439.164,30.877,419.061,11.643z"/>
											<path d="M150.175,266.736c-11.455-6.818-22.257-10.794-32.808-12.057c-2.466-0.295-4.918-0.443-7.361-0.443
										c-8.065,0-16.189,1.62-24.149,4.817c-30.825,12.389-33.835,41.568-36.491,67.315c-3.306,32.045-6.979,52.036-39.43,58.957
										c-5.942,1.268-10.125,6.608-9.93,12.682c0.195,6.074,4.711,11.136,10.723,12.02c18.16,2.67,35.401,4.023,51.246,4.024
										c0.004,0,0.007,0,0.011,0c34.558,0,63.052-6.296,84.689-18.712c19.855-11.393,33.144-27.572,38.43-46.788
										c2.911-10.582,3.135-21.488,1.005-31.951C182.025,296.534,169.276,278.103,150.175,266.736z"/>
									</svg>
								</div>
								<button type="button"
										class="button-icon button-icon-delete"
										:class="{ confirm: removingPlaylist !== null && removingPlaylist.id === playlist.id }"
										@click="removePlaylist(playlist)">
									<IconTimes /><span>{{ $t('playlist.removeButton') }}</span>
								</button>
							</template>
						</div>
					</div>
				</div>
			</div>
		</div>
	</modal>
</template>

<script>
	import { mapState } from "vuex";
	import IconTimes from "@/components/icons/IconTimes";
	import IconPen from "@/components/icons/IconPen";
	import IconPlus from "@/components/icons/IconPlus";
	import IconCheck from "@/components/icons/IconCheck";
	import Vue from "vue";
	import { PlaylistService } from "@/services/PlaylistService";

	export default {
		data() {
			return {
				editingPlaylist: null,
				removingPlaylist: null,
				removingPlaylistTimeout: null
			}
		},
		components: {
			IconTimes, IconPen, IconPlus, IconCheck
		},
		computed: {
			...mapState([ "playlists" ]),
			colors() {
				return [
					"#ce6c6c",
					"#000000",
					"#53b367",
					"#be3ede",
					"#cdde43",
					"#d68306",
					"#ffffff"
				]
			}
		},
		methods: {
			changeColor(playlist) {
				const currentColor = "color" in playlist ? playlist.color : null;
				let nextColor = null;
				if (currentColor === null) {
					nextColor = this.colors[0];
				} else {
					const nextKey = this.colors.indexOf(currentColor) + 1;
					nextColor = nextKey in this.colors ? this.colors[nextKey] : null;
				}

				playlist.color = nextColor;
				PlaylistService.setPlaylist(playlist);
			},
			stylePlaylistColor(playlist) {
				if (!("color" in playlist) || playlist.color === null) return {};
				return { "background-color": playlist.color };
			},
			createPlaylist() {
				this.$modal.show("create-playlist");
			},
			editPlaylist(playlist) {
				this.editingPlaylist = playlist;
				Vue.nextTick(() => {
					this.$refs.editPlaylistInput[0].focus();
				});
			},
			removePlaylist(playlist) {
				if (this.removingPlaylist !== null && this.removingPlaylist.id === playlist.id) {
					if (this.removingPlaylistTimeout !== null) clearTimeout(this.removingPlaylistTimeout);
					PlaylistService.removePlaylist(this.removingPlaylist);
					this.$notify({
						type: "success",
						text: this.$t('playlist.deleted'),
					});
					this.removingPlaylist = null;
				}

				this.removingPlaylist = playlist;
				if (this.removingPlaylistTimeout !== null) clearTimeout(this.removingPlaylistTimeout);
				this.removingPlaylistTimeout = setTimeout(() => {
					this.removingPlaylist = null;
				}, 4000);
			},
			savePlaylist() {
				PlaylistService.setPlaylist(this.editingPlaylist);
				this.editingPlaylist = null;
				this.$notify({
					type: "success",
					text: this.$t('playlist.renamed'),
				});
			}
		}
	}
</script>

<style lang="scss" scoped>
	.playlists-settings-modal {
		display: flex;
		flex-direction: column;
		background-color: #264653;
		color: hsla(0,0%,100%,.8);
		height: 100%;

		.playlists-header {
			display: flex;
			flex-direction: row;

			h1 {
				margin: 0;
				padding: 20px;
				font-size: 1.4em;
				font-weight: 100;
			}

			.close-modal {
				fill: white;
				width: 20px;
				opacity: 0.5;
				position: absolute;
				top: 20px;
				right: 20px;
				cursor: pointer;

				&:hover {
					opacity: 1;
				}
			}
		}

		input {
			width: 120px;
			height: 40px;
			padding: 0 20px;
			line-height: 50px;
			background-color: rgba(255, 255, 255, 0.21);
			font-weight: 300;
			color: rgba(255, 255, 255, 0.8);
			border: 0;
			margin-bottom: 1px;
			border-radius: 4px;

			&::placeholder {
				color: rgba(255, 255, 255, 0.49);
			}

			&:focus {
				outline: none;
			}
		}

		.playlists-container {
			overflow-y: auto;

			.playlists {
				display: flex;
				flex-direction: column;

				.playlist {
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: space-between;
					padding: 10px 20px;
					gap: 10px;

					&:hover {
						background-color: hsla(0,0%,100%,.11);
					}

					.left {

					}

					.right {
						display: flex;
						flex-direction: row;
						align-items: center;
						justify-content: space-between;
						gap: 10px;
					}

					div.playlist-color-select {
						width: 24px;
						height: 24px;
						border-radius: 12px;
						cursor: pointer;
						opacity: 0.7;
						padding: 0;
						font-size: 26px;
						line-height: 20px;

						svg {
							margin: 1px 0 0 1px;
							fill: white;
							width: 90%;
							height: 90%;
						}

						&.active {
							opacity: 1;

							svg {
								display: none;
							}
						}

						&:hover {
							opacity: 1;
						}
					}
				}
			}

		}

		button.button-icon {
			width: 24px;
			height: 25px;
			padding: 0;
			border: 0;
			cursor: pointer;
			border-radius: 4px;
			display: flex;
			flex-direction: row;
			align-items: center;
			align-content: center;
			text-align: center;
			justify-content: center;

			.icon {
				width: 12px;
				fill: rgba(255, 255, 255, 0.85);
			}

			&:focus {
				outline: none;
			}

			span {
				font-size: 0.8em;
				color: rgba(255, 255, 255, 0.85);
			}

			&.button-icon-delete {
				background-color: #d06871;

				-webkit-transition: all 0.2s;
				-moz-transition: all 0.2s;
				-o-transition: all 0.2s;
				transition: all 0.2s;

				&:hover {
					background-color: #b1595c;
				}

				span {
					display: none;
				}

				&.confirm {
					width: 72px;

					.icon {
						display: none;
					}

					span {
						display: block;
					}
				}
			}

			&.button-icon-edit {
				background-color: #e59253;

				&:hover {
					background-color: #bf754a;
				}
			}

			&.button-icon-check {
				background-color: #408d7b;

				&:hover {
					background-color: #367060;
				}
			}

			&.button-icon-add {
				background-color: #408d7b;
				width: 127px;
				text-align: right;
				margin: 22px 17px 15px 0;

				&:hover {
					background-color: #367060;
				}

				span {
					padding: 0;
					margin: 1px 0 0 4px;
				}
			}
		}
	}
</style>
